/**
 * HAL 9000 banner — progressive enhancement over server-rendered markup.
 *
 * The whole banner, the eye included, ships in the HTML (templates/hal-9000.php).
 * This entry only adds what markup cannot do: drive the lens layers from the
 * pointer. (The CTA is now a plain tel: link to HAL 9000's phone line — the
 * browser dials it, so no JS is involved there.)
 *
 * There is no framework and no renderer. The eye used to be a three.js scene,
 * which put ~130KB gzip behind a lazy chunk to draw one lens — 98.5% of that
 * payload was the engine. It is now five layers baked out of that same scene
 * and moved with CSS transforms, at ~23KB of images and ~1KB of code, and it is
 * on screen at first paint instead of a second later.
 */
import "./index.css";
import { mount2dEye } from "./lens2d.js";

function mount(root) {
  if (root.dataset.halMounted) return; // idempotent: safe if this runs twice
  root.dataset.halMounted = "1";

  /* The CTA is a native tel: link to HAL 9000's support line (see the
     template), so there is nothing to wire up here — the browser dials it. It
     used to intercept the click to open the arsenal-pc-ai launcher (#apc-btn),
     but that is the SALES chat, a different assistant; HAL is the phone agent. */

  const eye = root.querySelector(".hal-eye");
  if (eye) mountEye(eye);
}

/**
 * Bring the eye on screen as one piece, the SAME way every load. The five
 * layers are separate images that decode independently, so left alone they pop
 * in one at a time. Instead we hide the eye, decode them all (img.decode() +
 * Promise.all), fade the whole thing in together, and only then start the
 * motion. One path for warm and cold caches alike — on a warm cache the decode
 * just resolves in a microtask — so the load-in is deterministic rather than
 * branching on what happens to be cached.
 */
function mountEye(eye) {
  const imgs = [...eye.querySelectorAll("img")];
  let started = false;

  // Once the reveal fade is done the eye comes alive in two stages, mirroring
  // how raptorlabz keeps its heavy scene smooth: the CSS-only part first (it is
  // jank-proof), then the JS part (it is not), which we hold back until the main
  // thread is actually free.
  const goLive = () => {
    // The emissive "breathing" is a compositor-only CSS animation (index.css),
    // so it runs perfectly smoothly no matter how busy the main thread is. It is
    // the eye's jank-proof idle state — start it the instant the fade completes.
    eye.classList.add("is-live");

    // The pointer parallax, by contrast, is a per-frame rAF loop on the MAIN
    // thread. Starting it during the host page's load storm (Elementor init, the
    // image-lazyload reflow, other island hydration) is what made the eye choppy
    // for the first second or two. Defer it to the first idle period instead: on
    // a fast/cached page that is ~immediately; on a heavy load it waits the storm
    // out — either way the motion begins already-smooth, and the eye is visible
    // and breathing the whole time.
    whenIdle(() => mount2dEye(eye));
  };

  const reveal = () => {
    if (started) return;
    started = true;
    eye.classList.remove("is-loading"); // triggers the CSS opacity fade
    window.setTimeout(goLive, 380); // let the fade finish, then move
  };

  // One choreography regardless of cache: hide immediately, reveal once every
  // layer has decoded.
  eye.classList.add("is-loading");
  if (imgs.length) {
    Promise.all(
      imgs.map((im) => (im.decode ? im.decode().catch(() => {}) : Promise.resolve()))
    ).then(reveal);
    window.setTimeout(reveal, 4000); // safety net if a decode never settles
  } else {
    reveal();
  }
}

/**
 * Run cb once the main thread is next idle — i.e. after the page's load work has
 * drained. requestIdleCallback's own `timeout` guarantees it still fires on a
 * page that never goes fully idle, so the eye always ends up interactive; the
 * setTimeout is the fallback for engines without rIC. This is what keeps the
 * eye's JS motion off the busy main thread during load WITHOUT branching on
 * cache state — a warm page idles at once, a cold one waits, neither janks.
 */
function whenIdle(cb) {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(cb, { timeout: 2000 });
  } else {
    window.setTimeout(cb, 800);
  }
}

function init() {
  // Every instance on the page, not a single hardcoded id — two shortcodes on
  // one page used to produce duplicate ids and a permanently empty second box.
  document.querySelectorAll(".arsenal-hal-container").forEach(mount);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
